import { useEffect, useRef, useState } from 'react';
import {
  type FieldError,
  type FieldValues,
  FormProvider,
  useForm,
  useFormState,
} from 'react-hook-form';
import { BorrowerInputVariables, PortalType } from '@sparrow-card/sparrow-card-constants';
import { type FormInputsRefContextType, FormInputsRefContext } from 'contexts/inputsRefContext';
import { StaticErrorMessage } from 'enums/Messages';
import ApplyLayout from 'layout/ApplyLayout';
import { registerFields } from 'pages/Apply/common/commonFieldsValidation';
import Form from './components/Form';
import {
  DEMO_TEXT_MESSAGES_CHECKBOX,
  DemoApplyFormRequiredFields,
} from './components/Form/formConstants';
import styles from './DemoApply.module.scss';

const DEMO_ERROR_ORDER = [
  BorrowerInputVariables.FirstName,
  BorrowerInputVariables.LastName,
  BorrowerInputVariables.DateOfBirth,
  BorrowerInputVariables.Phone,
  BorrowerInputVariables.Street,
  BorrowerInputVariables.Unit,
  BorrowerInputVariables.City,
  BorrowerInputVariables.State,
  BorrowerInputVariables.Zip,
  BorrowerInputVariables.SSN,
  BorrowerInputVariables.TotalMonthlyIncome,
  BorrowerInputVariables.CheckBoxTerms,
  BorrowerInputVariables.CheckBoxElectronicCommunications,
  BorrowerInputVariables.CheckBoxContracted,
  DEMO_TEXT_MESSAGES_CHECKBOX,
  BorrowerInputVariables.CheckBoxCreditReport,
];

const DemoApply = () => {
  const inputRefs = useRef<FormInputsRefContextType>({});
  const [serverErrors, setServerErrors] = useState<Partial<Record<BorrowerInputVariables, string>>>(
    {},
  );

  const methods = useForm<FieldValues>({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    shouldFocusError: false,
    defaultValues: {
      [BorrowerInputVariables.FirstName]: '',
      [BorrowerInputVariables.MiddleInitial]: '',
      [BorrowerInputVariables.LastName]: '',
      [BorrowerInputVariables.DateOfBirth]: '',
      [BorrowerInputVariables.Phone]: '',
      [BorrowerInputVariables.Street]: '',
      [BorrowerInputVariables.Unit]: '',
      [BorrowerInputVariables.City]: '',
      [BorrowerInputVariables.State]: '',
      [BorrowerInputVariables.Zip]: '',
      [BorrowerInputVariables.SSN]: '',
      [BorrowerInputVariables.TotalMonthlyIncome]: '',
      [BorrowerInputVariables.CheckBoxTerms]: false,
      [BorrowerInputVariables.CheckBoxElectronicCommunications]: false,
      [BorrowerInputVariables.CheckBoxContracted]: false,
      [DEMO_TEXT_MESSAGES_CHECKBOX]: false,
      [BorrowerInputVariables.CheckBoxCreditReport]: false,
    },
  });

  const { control, clearErrors, setError, register, trigger, unregister } = methods;

  useFormState({ control });

  const registerDemoFields = () => {
    registerFields(
      methods,
      serverErrors,
      setServerErrors,
      DemoApplyFormRequiredFields,
      PortalType.Standard,
    );
    // Demo form has no email field — drop hidden email validation.
    unregister(BorrowerInputVariables.Email);
    register(DEMO_TEXT_MESSAGES_CHECKBOX, {
      required: {
        value: true,
        message: StaticErrorMessage.UnCheckedCheckBox,
      },
    });
  };

  useEffect(() => {
    registerDemoFields();
  }, [serverErrors]);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Controller remounts can wipe register() rules; restore them before validating.
    registerDemoFields();

    const isValid = await trigger();

    if (isValid) {
      // intentional no-op for demo
      return;
    }

    const currentErrors = methods.formState.errors;

    for (const field of DEMO_ERROR_ORDER) {
      const error = currentErrors[field] as FieldError | undefined;

      if (error?.message) {
        setError(field, { type: error.type ?? 'manual', message: error.message });
      }
    }

    const firstInvalidField = DEMO_ERROR_ORDER.find((field) => currentErrors[field]);

    if (firstInvalidField) {
      // Scroll without focusing — RootInput clears errors on focus.
      inputRefs.current[firstInvalidField]?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  };

  return (
    <ApplyLayout>
      <div className={styles.formWrapper}>
        <FormProvider {...methods}>
          <FormInputsRefContext.Provider value={inputRefs}>
            <Form
              handleSubmit={onSubmit}
              control={control}
              clearErrors={clearErrors}
              setError={setError}
            />
          </FormInputsRefContext.Provider>
        </FormProvider>
      </div>
    </ApplyLayout>
  );
};

export default DemoApply;
